import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "../../api";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../utils/apiError";
import { CustomerProduct } from "../../types";
import { DELIVERY_STATUSES, PAYMENT_STATUSES, PRODUCT_STATUSES, PRODUCT_TYPES } from "../../constants/enums";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { FilterSelect, SearchInput, Toolbar } from "../../components/ui/Toolbar";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import CustomerProductTable from "./CustomerProductTable";
import CustomerProductFormModal from "./CustomerProductFormModal";

export default function CustomerProductsPage() {
    const { hasRole } = useAuth();
    const isAdmin = hasRole("SUPERADMIN");
    const toast = useToast();

    // Filters live in the URL so dashboard links like ?paymentStatus=NOT_PAID work
    const [params, setParams] = useSearchParams();
    const filter = (key: string) => params.get(key) ?? "";
    const setFilter = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        // delivery only applies to hardware, status only to software
        if (key === "type" && value === "SOFTWARE") next.delete("deliveryStatus");
        if (key === "type" && value === "HARDWARE") next.delete("status");
        setParams(next, { replace: true });
    };

    const type = filter("type");
    const { data, loading, error, reload } = useApi<CustomerProduct[]>("/customer-product", {
        type,
        status: filter("status"),
        paymentStatus: filter("paymentStatus"),
        deliveryStatus: filter("deliveryStatus"),
    });

    const [search, setSearch] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<CustomerProduct | null>(null);
    const [deleting, setDeleting] = useState<CustomerProduct | null>(null);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!data) return null;
        if (!q) return data;
        return data.filter((r) =>
            [r.customer.name, String(r.customer.customerId), r.product.name, r.product.category?.name, r.subscriptionPlan?.name, r.deviceType?.name]
                .filter(Boolean)
                .some((v) => v!.toLowerCase().includes(q))
        );
    }, [data, search]);

    const handleDelete = async () => {
        if (!deleting) return;
        try {
            await api.delete(`/customer-product/${deleting.id}`);
            toast.success("Removed from customer");
            setDeleting(null);
            reload();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const anyFilter = !!(search || Array.from(params.keys()).length);

    return (
        <>
            <PageHeader
                title="Customer Products"
                subtitle="Hardware sold and software subscriptions, per customer."
                actions={
                    <Button
                        icon={Plus}
                        onClick={() => {
                            setEditing(null);
                            setFormOpen(true);
                        }}
                    >
                        Assign product
                    </Button>
                }
            />

            <Card padded={false}>
                <Toolbar>
                    <SearchInput value={search} onChange={setSearch} placeholder="Customer, product, plan…" />
                    <FilterSelect value={type} onChange={(v) => setFilter("type", v)} options={PRODUCT_TYPES} allLabel="All types" />
                    {type !== "HARDWARE" && (
                        <FilterSelect value={filter("status")} onChange={(v) => setFilter("status", v)} options={PRODUCT_STATUSES} allLabel="Any status" />
                    )}
                    {type !== "SOFTWARE" && (
                        <FilterSelect
                            value={filter("deliveryStatus")}
                            onChange={(v) => setFilter("deliveryStatus", v)}
                            options={DELIVERY_STATUSES}
                            allLabel="Any delivery"
                        />
                    )}
                    <FilterSelect
                        value={filter("paymentStatus")}
                        onChange={(v) => setFilter("paymentStatus", v)}
                        options={PAYMENT_STATUSES}
                        allLabel="Any payment"
                    />
                    {anyFilter && (
                        <button
                            onClick={() => {
                                setSearch("");
                                setParams({}, { replace: true });
                            }}
                            className="text-xs font-semibold text-[#a78bfa] hover:text-white px-2"
                        >
                            Clear
                        </button>
                    )}
                    {data && <span className="text-xs text-slate-500 sm:ml-auto">{rows?.length ?? 0} records</span>}
                </Toolbar>

                <CustomerProductTable
                    rows={rows}
                    loading={loading}
                    error={error}
                    onRetry={reload}
                    onEdit={
                        isAdmin
                            ? (r) => {
                                  setEditing(r);
                                  setFormOpen(true);
                              }
                            : undefined
                    }
                    onDelete={isAdmin ? setDeleting : undefined}
                    emptyTitle={anyFilter ? "No matching records" : "Nothing assigned yet"}
                    emptyText={anyFilter ? "Try changing the filters." : "Assign hardware or a software subscription to a customer."}
                />
            </Card>

            <CustomerProductFormModal
                open={formOpen}
                item={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => {
                    setFormOpen(false);
                    reload();
                }}
            />

            <ConfirmDialog
                open={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={handleDelete}
                title="Remove product from customer?"
                message={
                    <>
                        <b className="text-white">{deleting?.product.name}</b> will be removed from{" "}
                        <b className="text-white">{deleting?.customer.name}</b>. This can't be undone.
                    </>
                }
                confirmLabel="Remove"
            />
        </>
    );
}